/**
 * Undo/redo history and element edits for the annotation editor.
 *
 * Pure — no React, no DOM. The editor holds one `AnnotationHistory` in state and
 * routes every mutation through these functions, so the history semantics are
 * unit-testable without mounting anything.
 *
 * History is whole-document snapshots rather than inverse commands. With
 * `MAX_ANNOTATIONS = 200` and a few hundred points per element that is trivially
 * cheap, and it removes an entire class of bug (an inverse command that does not
 * quite invert) for no meaningful memory cost.
 *
 * In-progress geometry deliberately does NOT live here. The editor keeps the
 * half-drawn shape in a separate `draft`, so a stroke mid-gesture is never an
 * undo step and can never reach the POST body.
 */

import type { ImageAnnotationDict } from '../../../../../generated/nodeDataTypes'

/** Bound on retained undo steps. Past this, the oldest snapshot is dropped. */
export const MAX_HISTORY_DEPTH = 50

export interface AnnotationHistory {
  past: ImageAnnotationDict[][]
  present: ImageAnnotationDict[]
  future: ImageAnnotationDict[][]
}

export function createHistory(present: ImageAnnotationDict[] = []): AnnotationHistory {
  return { past: [], present, future: [] }
}

/**
 * Push a new document state, making the previous one undoable.
 *
 * Clears the redo stack: once the user takes a new action, the branch they
 * undid away is gone. This is the conventional behaviour and the one the canvas
 * undo stack already implements.
 */
export function commit(
  history: AnnotationHistory,
  next: ImageAnnotationDict[],
): AnnotationHistory {
  const past = [...history.past, history.present]
  return {
    past: past.length > MAX_HISTORY_DEPTH ? past.slice(past.length - MAX_HISTORY_DEPTH) : past,
    present: next,
    future: [],
  }
}

export function canUndo(history: AnnotationHistory): boolean {
  return history.past.length > 0
}

export function canRedo(history: AnnotationHistory): boolean {
  return history.future.length > 0
}

export function undo(history: AnnotationHistory): AnnotationHistory {
  if (history.past.length === 0) return history
  const previous = history.past[history.past.length - 1]
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
  }
}

export function redo(history: AnnotationHistory): AnnotationHistory {
  if (history.future.length === 0) return history
  const next = history.future[0]
  return {
    past: [...history.past, history.present],
    present: next,
    future: history.future.slice(1),
  }
}

/** Remove one element by id. Returns the same array reference when nothing matched. */
export function removeElement(
  elements: ImageAnnotationDict[],
  id: string,
): ImageAnnotationDict[] {
  const next = elements.filter((element) => element.id !== id)
  return next.length === elements.length ? elements : next
}

/**
 * Translate every point of one element by a percent delta.
 *
 * Coordinates are clamped to 0-100 so a drag that leaves the image cannot author
 * an out-of-range spec — the renderer rejects those rather than clamping them,
 * and a rejected save at the end of a drag is a poor way to learn that. Clamping
 * per-point (rather than refusing the whole drag) matches how every other canvas
 * drag behaves: the shape squashes against the edge instead of snapping back.
 */
export function translateElement(
  element: ImageAnnotationDict,
  deltaXPct: number,
  deltaYPct: number,
): ImageAnnotationDict {
  return {
    ...element,
    points: element.points.map((point) => ({
      x: Math.min(100, Math.max(0, point.x + deltaXPct)),
      y: Math.min(100, Math.max(0, point.y + deltaYPct)),
    })),
  }
}

/** Replace one element in place, preserving array (and therefore draw) order. */
export function replaceElement(
  elements: ImageAnnotationDict[],
  next: ImageAnnotationDict,
): ImageAnnotationDict[] {
  return elements.map((element) => (element.id === next.id ? next : element))
}

/**
 * Generate an element id.
 *
 * The renderer rejects duplicate ids across a spec, so this needs to be unique
 * within one editing session. A counter plus the kind is enough and keeps ids
 * readable in NAD traces; `Math.random()` would make the parity fixture
 * non-reproducible.
 */
export function makeElementId(kind: string, sequence: number): string {
  return `${kind}-${sequence}`
}
